import { Camera, Ellipsis, Eye } from 'lucide-react';
import noImage from 'images/no-image.jpg';
import * as React from 'react';
import ProductsTemplateEn from 'templates/import_products_en.csv';
import ProductsTemplateFr from 'templates/import_products_fr.csv';
import { getTranslate, Translate } from 'react-localize-redux';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { useRecoilState, useRecoilValue } from 'recoil';
import swal from 'sweetalert2';
import { AInputSearch } from '../../atoms/filter/InputSearchFilter';
import { AFormFields } from '../../atoms/forms';
import { AProducts } from '../../atoms/product';
import equal from 'deep-equal';
import AdvancedFilters from '../../components_v2/filter/AdvancedFilters';
import { FilterId, FilterResult, filterTreeLength } from '../../components_v2/filter/model/Model';
import { convertFilters } from '../../components_v2/filter/pages/FilterList';
import PopupDeletion from '../../components_v2/popup/PopupDeletion';
import PopupView, { ViewCreation } from '../../components_v2/view/PopupView';
import ViewPicker from '../../components_v2/view/ViewPicker';
import { AViews, DbView, deleteView, getViews, NewView, postView, ProductsView, putView, ViewCompanyColumns } from '../../atoms/global/views';
import { useFunctionState } from '../../utils/customHooks';
import { Column as OrganizerColumn, ColumnOrganizerWrapper } from '../client-companies/popup/ColumnOrganizer';
import { SidebarButton } from '../client-companies/style/Style';
import ToolbarFilterButton from '../../components_v2/toolbarFilter/ToolbarButton';
import { getProductFieldValues } from '../promotions/actions';
import { collectProductDimFieldIds, matchProductTree, productFilterList } from '../promotions/perimeterFilters';
import { launchToastError } from '../forms/visual-form-editor/visualFormEditor';
import useAlert from '../alert/UseAlert';
import importProducts from './importProducts';
import { clearProductDimensionValuesCache } from './action';
import { defaultFilters } from '../reports/interpretor/ReportInterpretor';
import { AtomCategory } from '../../atoms/utils/model/Model';
import Dropdown from '../../components_v2/dropdown/Dropdown';
import { DropdownData } from '../../components_v2/dropdown/model/Model';
import { Checkbox } from '../../components_v2/filterList/style/Style';
import PageLoader from '../../components_v2/pageLoader/PageLoader';
import Pagination from '../../components_v2/pagination/Pagination';
import { Table, TableSortType } from '../../components_v2/table/Table';
import ToolbarFilter, { ToolbarElement } from '../../components_v2/toolbarFilter/ToolbarFilter';
import { getUrlParameterByName, isSuperAdmin, removeAllUrlParameters } from '../../components_v2/utils';
import storeLang from '../../helpers/storeLang';
import { DarkGreySidely, GreySidely3, RedSidely } from '../../styles/global/css/Utils';
import { translateToString } from '../../styles/global/translate';
import { exportData } from '../client-companies/data/action';
import { TableCellButton } from '../client-companies/style/Style';
import { TOOLBARS_HEIGHT, ToolbarImageContainer } from '../globals/defaultToolbar/style/Style';
import { ToolbarState } from '../globals/mainPage/mainPage';
import { LoadingState } from '../import/model';
import NoData, { Unhautorized } from '../noData/NoData';
import { TypeFile } from '../orders/model/Model';
import { TableRow, TableRowStatusBackground } from '../orders/templateOrders/style/Style';
import PermissionContext from '../permissions/PermissionContext';
import Restricted from '../permissions/Restricted';
import usePermission from '../permissions/usePermission';
import { formatCurrency, getCurrencySymbol, getLanguageCode } from '../reports/utils';
import { get_products, getProductDimensionValues, getProductTaxes, patchProductDimensionValue, saveAdditionalValue, updateProduct, updateProducts } from './action';
import * as moment from 'moment';
import { Field } from '../../atoms/forms';
import { getTaxes } from '../settings/actions';
import { Tax } from '../settings/models';
import DropdownTagsCloud from '../../components_v2/dropdown/DropdownTagsCloud';
import LightDropdown from '../../components_v2/dropdown/lightDropdown/LightDropdown';
import Switch from '../../components_v2/Switch/Switch';
import { DefaultTextDiv } from '../../styles/global/css/GlobalText';
import { DateAccessor, EditableContent } from '../client-companies/data/CellEditors';
import { ModalState, Product } from './model';
import ProductPopup from './product';
import ProductCreation from './productCreation';
import { Container, FlexDiv, Link, LoaderContainer, Status, TABLE_IMAGE_PADDING, TABLE_IMAGE_WIDTH } from './style';
import VisionPopup from './vision/VisionPopup';
import { ToolbarToggle, VisionOpen } from './vision/style';
import Add from '../../components_v2/add/Add';


// The product-level value of a dimension field, displayed and edited with
// the same per-type components as the client companies additional columns.
// Storage matches the assortments editor: Money in cents, Date as ISO,
// Multiselect as a string array.
function formatDimensionValue(field: Field, value: unknown): string {
	if (value === undefined || value === null || value === '') return '-';
	if (field.type === 'Money' && typeof value === 'number') return formatCurrency(value / 100);
	if (field.type === 'Date' && typeof value === 'string') return moment(value).format('L');
	if (Array.isArray(value)) return value.join(', ');
	return String(value);
}

// Sizes are numerics the API serializes as strings
function formatSize(value: number | string | undefined | null): string {
	if (value === undefined || value === null || value === '') return '-';
	const parsed = Number(value);
	return isNaN(parsed) ? '-' : `${parsed} cm`;
}

function formatTax(tax: Tax, value: number | undefined): string {
	if (value === undefined || value === null) return '-';
	switch (tax.tax_type_name) {
		case 'percentage': return `${value} %`;
		case 'amount': return `${value} ${getCurrencySymbol()}`;
		default: return String(value);
	}
}

function DimensionCell(props: { field: Field, uuid: string, rowId: number, value: unknown, editable: boolean, onSaved: (fieldId: number, uuid: string, value: unknown) => void }) {
	const { field, uuid, value } = props;
	const save = (v: unknown) => saveAdditionalValue({ p: uuid, f: field.id }, v)
		.then(() => props.onSaved(field.id, uuid, v))
		.catch(console.error);
	if (!props.editable) return <TableRow>{formatDimensionValue(field, value)}</TableRow>;
	switch (field.type) {
		case 'Text':
		case 'Url':
		case 'Email':
		case 'Phone':
		case 'Address':
		case 'TextArea':
			return <EditableContent<Product>
				innerId={`${props.rowId}-${field.id}-${String(value ?? '')}`}
				row={undefined}
				valueKey={`dim[${field.id}]`}
				defaultValue={value == null ? '' : String(value)}
				onValueUpdate={v => save(v ? v : null)}
			/>;
		case 'Integer':
			return <EditableContent<Product>
				innerId={`${props.rowId}-${field.id}-${String(value ?? '')}`}
				row={undefined}
				valueKey={`dim[${field.id}]`}
				defaultValue={value == null ? '' : String(value)}
				// a non-number snaps back instead of saving (and showing) NaN
				regex={v => v.trim() === '' || /^-?\d+$/.test(v.trim())}
				onValueUpdate={v => {
					const parsed = v === null ? null : parseInt(v);
					if (parsed === null || !isNaN(parsed)) save(parsed);
				}}
			/>;
		case 'Number':
			return <EditableContent<Product>
				innerId={`${props.rowId}-${field.id}-${String(value ?? '')}`}
				row={undefined}
				valueKey={`dim[${field.id}]`}
				defaultValue={value == null ? '' : String(value)}
				regex={v => v.trim() === '' || /^-?\d+([.,]\d+)?$/.test(v.trim())}
				onValueUpdate={v => {
					const parsed = v === null ? null : parseFloat(v.replaceAll(',', '.'));
					if (parsed === null || !isNaN(parsed)) save(parsed);
				}}
			/>;
		case 'Money':
			return <EditableContent<Product>
				innerId={`${props.rowId}-${field.id}-${String(value ?? '')}`}
				row={undefined}
				valueKey={`dim[${field.id}]`}
				defaultValue={typeof value === 'number' ? (value / 100).toFixed(2) : ''}
				regex={v => v.trim() === '' || /^-?\d+([.,]\d+)?$/.test(v.trim())}
				onValueUpdate={v => {
					const parsed = v === null ? null : parseFloat(v.replaceAll(',', '.'));
					if (parsed === null || !isNaN(parsed)) save(parsed === null ? null : Math.round(parsed * 100));
				}}
			/>;
		case 'Boolean':
			return <Switch value={value === true || value === 'true'} onChange={save} />;
		case 'Date':
			return <DateAccessor
				date={typeof value === 'string' && value ? value : undefined}
				onDateChange={date => save(date.toISOString())}
				// fixed and centered: rendered in-flow, the picker is clipped by
				// the sticky header on the first rows and by the table overflow
				pickerProps={{ style: { position: 'fixed', zIndex: 30, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' } }}
			/>;
		case 'Select':
			return <LightDropdown
				dropdownStyle={{ containerHeight: 'unset', optionHeight: '250px', optionWidth: '200px' }}
				datalist={(Array.isArray(field.data) ? field.data : []).map(o => ({ label: o, value: o }))}
				JSXButton={({ onClick }) => <TableRow onClick={onClick} cursor='pointer'>{value == null || value === '' ? '-' : String(value)}</TableRow>}
				JSXContent={({ value, onClick }) => <div style={{ margin: 5 }}><TableRowStatusBackground onClick={onClick}>{value?.label}</TableRowStatusBackground></div>}
				onChange={v => save(v.value)}
			/>;
		case 'Multiselect': {
			let selected: string[] = [];
			try {
				selected = Array.isArray(value) ? value : JSON.parse(String(value ?? '[]'));
			} catch { selected = []; }
			return <DropdownTagsCloud
				addImageSize='1.25em'
				NoneContainer={<DefaultTextDiv>-</DefaultTextDiv>}
				autoOptionUp
				horizontal
				noSearch
				addImageOnHover
				dropdownSearch
				selected={selected.map(o => ({ name: o, id: o }))}
				onChange={values => save(values.map(t => t.name))}
				onDelete={v => save(selected.filter(o => o !== v))}
				tags={(Array.isArray(field.data) ? field.data : []).map(o => ({ name: o, id: o }))}
			/>;
		}
	}
	return <TableRow>{formatDimensionValue(field, value)}</TableRow>;
}

// the identity and action columns never move nor hide
const PINNED_START = ['select', 'photo', 'name'];
const PINNED_COLUMNS = [...PINNED_START, 'actions'];

type DimensionColumns = {
	fields: Field[],
	values: Map<number, Map<string, unknown>>,
	uuidById: Map<number, string>,
	editable: boolean,
	onSaved: (fieldId: number, uuid: string, value: unknown) => void,
};

// the taxes configured in the settings and their per-product values
// (tax id -> product id -> value), the same rows the product popup shows
type TaxColumns = {
	taxes: Tax[],
	values: Map<number, Map<number, number | undefined>>,
};

// secondary product columns start hidden until the user configures columns
const DEFAULT_HIDDEN_COLUMNS = ['stock', 'outer', 'created_at', 'updated_at'];

const DEFAULT_LIMIT = 50;
// One wide page fetched when an advanced filter tree is active (the tree is
// evaluated client-side; org product counts fit comfortably).
const ADVANCED_FILTER_FETCH_LIMIT = 10000;

function productcolumns(products: Product[], setProducts: (products: Product[]) => void, refresh: () => void, openProduct: (product: Product, full: boolean) => void, dimensions: DimensionColumns, taxes: TaxColumns, organizer: JSX.Element, openVision?: (product: Product) => void) {
	const translate = getTranslate(storeLang.getState().localize);
	const DELETE = 1;
	const all_checked = products.every(row => row.checked);
	const list = [
		{
			label: translate('global.quick_action.delete').toString(),
			value: DELETE
		}
	];
	return [
		{
			id: 'select',
			disableFilter: true,
			freeze: 'left' as const,
			Header: <Checkbox isActive={all_checked} onClick={() => {
				if (all_checked) {
					setProducts(products.map(p => ({ ...p, checked: false })));
				} else {
					setProducts(products.map(p => ({ ...p, checked: true })));
				}
			}} />,
			accessor: (row: Product) => {
				return (
					<Checkbox isActive={row.checked ?? false} onClick={() => {
						const index = products.findIndex(p => p.id == row.id);
						if (index >= 0) {
							products[index].checked = !(row.checked ?? false);
							setProducts([...products]);
						}
					}} />
				);
			},
			width: 40,
			disableSortBy: true,
			unresizeable: true
		},
		{
			id: 'photo',
			disableFilter: true,
			freeze: 'left' as const,
			Header: ' ',
			accessor: (row: Product) => <Link centered onClick={() => openProduct(row, true)} background={row.photos_url_id?.[0]?.[0] ?? noImage} ></Link>,
			disableSortBy: true,
			width: TABLE_IMAGE_WIDTH,
			padding: TABLE_IMAGE_PADDING.DEFAULT,
			unresizeable: true
		},
		{
			id: 'name',
			filterId: 'product_name',
			freeze: 'left' as const,
			Header: <Translate id='name' />,
			accessor: (row: Product) => <>
				<TableRow onClick={() => openProduct(row, true)} fontWeight="500" color={DarkGreySidely} cursor="pointer">
					{row.name ?? '-'}
				</TableRow>
				<TableCellButton icon={Eye} onClick={() => openProduct(row, false)} />
				{openVision && <VisionOpen title={translate('products.vision_open').toString()} onClick={() => openVision(row)}><Camera size={18} /></VisionOpen>}
			</>,
			width: 326
		},
		{
			id: 'reference',
			filterId: 'product_reference',
			Header: <Translate id='reference' />,
			accessor: (row: Product) => <TableRow>{row.reference || '-'}</TableRow>,
			width: 140
		},
		{
			id: 'barcode',
			filterId: 'product_barcode',
			Header: <Translate id='EAN-JAN barcode' />,
			accessor: (row: Product) => <TableRow>{row.barcode || '-'}</TableRow>,
			width: 150
		},
		{
			id: 'code_upc',
			filterId: 'product_upc',
			Header: <Translate id='UPC Barcode' />,
			accessor: (row: Product) => <TableRow>{row.code_upc || '-'}</TableRow>,
			width: 150
		},
		{
			id: 'brand',
			filterId: { product_brand: null } as unknown as FilterId,
			Header: <Translate id='brand' />,
			accessor: (row: Product) => <TableRow>{row.brand_name ?? '-'}</TableRow>,
			width: 160
		},
		{
			id: 'category',
			filterId: { product_category: null } as unknown as FilterId,
			Header: <Translate id='category' />,
			accessor: (row: Product) => <TableRow>{row.category_name ?? '-'}</TableRow>,
			width: 160
		},
		{
			id: 'price',
			filterId: 'product_price',
			Header: <Translate id='price' />,
			accessor: (row: Product) => <TableRow>{row.price ? formatCurrency(row.price) : '-'}</TableRow>,
			width: 110
		},
		{
			id: 'recommended_price',
			disableFilter: true,
			Header: <Translate id='recommended price' />,
			accessor: (row: Product) => <TableRow>{row.recommended_price ? formatCurrency(row.recommended_price) : '-'}</TableRow>,
			width: 140
		},
		// one column per tax configured in the settings, like the product popup
		...taxes.taxes.map(tax => ({
			id: `tax_${tax.id}`,
			disableFilter: true,
			Header: <span title={tax.name}>{tax.name}</span>,
			accessor: (row: Product) => <TableRow>{formatTax(tax, taxes.values.get(tax.id)?.get(row.id))}</TableRow>,
			disableSortBy: true,
			width: 110,
		})),
		{
			id: 'stock',
			disableFilter: true,
			Header: <Translate id='stock' />,
			accessor: (row: Product) => <TableRow>{row.stock ?? '-'}</TableRow>,
			width: 100
		},
		{
			id: 'outer',
			disableFilter: true,
			Header: <Translate id='outer' />,
			accessor: (row: Product) => <TableRow>{row.outer ?? '-'}</TableRow>,
			width: 100
		},
		{
			id: 'width',
			filterId: 'product_width',
			Header: <><Translate id='width' /> (cm)</>,
			accessor: (row: Product) => <TableRow>{formatSize(row.width)}</TableRow>,
			width: 120
		},
		{
			id: 'height',
			filterId: 'product_height',
			Header: <><Translate id='height' /> (cm)</>,
			accessor: (row: Product) => <TableRow>{formatSize(row.height)}</TableRow>,
			width: 120
		},
		{
			id: 'depth',
			filterId: 'product_depth',
			Header: <><Translate id='depth' /> (cm)</>,
			accessor: (row: Product) => <TableRow>{formatSize(row.depth)}</TableRow>,
			width: 120
		},
		{
			id: 'status',
			disableFilter: true,
			Header: <Translate id='Avaibility' />,
			accessor: (row: Product) => <Status color={row.status_color_code}>{row.status_name ?? '-'}</Status>,
			width: 130
		},
		{
			id: 'created_at',
			disableFilter: true,
			Header: <Translate id='created_at' />,
			accessor: (row: Product) => <DateAccessor date={row.created_at} utc />,
			width: 150
		},
		{
			id: 'updated_at',
			disableFilter: true,
			Header: <Translate id='updated_at' />,
			accessor: (row: Product) => <DateAccessor date={row.updated_at} utc />,
			width: 150
		},
		// one column per multi-dimension field: its product-level value
		// ({p, f} path), editable in place exactly like the assortments grid
		...dimensions.fields.map(field => ({
			id: `dim_${field.id}`,
			filterId: { product_dim_field: field.id } as unknown as FilterId,
			Header: <span title={field.name}>{field.name}</span>,
			accessor: (row: Product) => {
				const uuid = dimensions.uuidById.get(row.id);
				if (!uuid) return <TableRow>-</TableRow>;
				return <DimensionCell
					field={field}
					uuid={uuid}
					rowId={row.id}
					value={dimensions.values.get(field.id)?.get(uuid)}
					editable={dimensions.editable}
					onSaved={dimensions.onSaved}
				/>;
			},
			disableSortBy: true,
			width: 160,
		})),
		// pinned on screen at any horizontal scroll, like the contacts
		// listing: a trailing 40px column was cropped and unreachable once
		// the dimension columns made the table overflow
		{
			id: 'actions',
			disableFilter: true,
			freeze: 'right' as const,
			// the gear opens the column organizer, like the companies listing;
			// noHeaderEllipsis: the default header wrapper crops it
			noHeaderEllipsis: true,
			Header: organizer,
			accessor: (row: Product) => <Restricted to={{ objectAction: 'DeleteCatalog' }}>
				<Dropdown
					name={'quick_action'}
					searchable={false}
					datalist={list}
					readOnly
					dropdownStyle={{ width: '10px', optionWidth: '100px', fontSize: 13, optionLeft: '-70px' }}
					JSXButton={() => <ToolbarImageContainer rotate={'90deg'} marginLeft='-3px'><Ellipsis color={GreySidely3} strokeWidth={2.5} size={24} /></ToolbarImageContainer>}
					onChange={(value: DropdownData) => {
						switch (value.value) {
							case DELETE:
								// @ts-expect-error swal
								swal.fire({
									title: 'Attention!',
									text: translate('global.fire.delete_message').toString().replace('{{NAME}}', `'${row.name}'`),
									type: 'warning',
									confirmButtonText: translate('global.yes'),
									confirmButtonColor: RedSidely
								})
									.then(result => {
										if (result.value) {
											updateProduct(row.id, { deleted: true })
												.then(refresh)
												.catch(console.log);
										}
									});
								break;
						}
					}} />
			</Restricted>,
			width: 40,
			disableSortBy: true,
			unresizeable: true
		}
	];
}

export default function ProductListing(props: {
	setToolBarState: (value: ToolbarState) => void
}): JSX.Element {
	const [products, setProducts] = React.useState<Product[]>([]);
	const [loadingState, setLoadingState] = React.useState<LoadingState>('loading');
	const [pagination, setPagination] = React.useState({ step: DEFAULT_LIMIT, offset: 0 });
	const [max, setMax] = React.useState<number>(0);
	const [sort, setSort] = React.useState<TableSortType | undefined>(undefined);
	const [modalState, setModalState] = React.useState<ModalState>({ isOpen: false });
	const [isCreationOpen, setIsCreationOpen] = React.useState<boolean>(false);
	const exportCreatable = usePermission({ objectAction: 'CreateCatalog' });
	const massDeletable = usePermission({ objectAction: 'DeleteCatalog' });
	const { isAllowedTo } = React.useContext(PermissionContext);
	const translate = getTranslate(storeLang.getState().localize);
	const dispatch = useDispatch();
	const alert = useAlert();


	const [inputSearchText, setInputSearchText] = useRecoilState(AInputSearch);
	// Advanced filters — same machinery as the promotion product picker: the
	// tree is evaluated client-side against the products atom (which carries
	// uuid/brand/category/custom fields), and the listing keeps only the
	// matching ids.
	const allProducts = useRecoilValue(AProducts);
	const allFields = useRecoilValue(AFormFields);
	// the fields flagged Product show as product-level columns; their values
	// live in additional_values under dimensionless {p, f} paths, shared with
	// the assortments page (whose dimensions keep the Additional flag)
	const dimensionFields = React.useMemo(() => allFields.filter(f => f.is_product), [allFields]);
	const [dimensionValues, setDimensionValues] = React.useState<Map<number, Map<string, unknown>>>(new Map());
	// the taxes configured in the settings, one column each; their per-product
	// values are refetched with every product page since the popup edits them
	const [taxes, setTaxes] = React.useState<Tax[]>([]);
	const [taxValues, setTaxValues] = React.useState<Map<number, Map<number, number | undefined>>>(new Map());
	React.useEffect(() => {
		getTaxes().then(setTaxes).catch(console.error);
	}, []);
	const fetchTaxValues = React.useCallback(() => getProductTaxes().then(rows => {
		const map = new Map<number, Map<number, number | undefined>>();
		rows.forEach(r => {
			if (r.product_id === undefined) return;
			if (!map.has(r.tax_id)) map.set(r.tax_id, new Map());
			map.get(r.tax_id)?.set(r.product_id, r.tax_value);
		});
		setTaxValues(map);
	}).catch(console.error), []);
	const productUuidById = React.useMemo(() => new Map(allProducts.map(p => [p.id, p.uuid])), [allProducts]);
	// superadmins can switch the listing to "vision mode": a camera next to each product opens
	// its shelf-vision images (packshots to add or remove, field crops to review)
	const amISuperAdmin = isSuperAdmin();
	const [visionMode, setVisionMode] = React.useState(false);
	const [visionProduct, setVisionProduct] = React.useState<{ uuid: string, name: string }>();
	const openVision = React.useMemo(() => visionMode && amISuperAdmin
		? (product: Product) => {
			const uuid = productUuidById.get(product.id);
			if (uuid) setVisionProduct({ uuid, name: product.name });
		}
		: undefined, [visionMode, amISuperAdmin, productUuidById]);
	const onDimensionValueSaved = React.useCallback((fieldId: number, uuid: string, value: unknown) => {
		patchProductDimensionValue(fieldId, uuid, value);
		setDimensionValues(prev => {
			const next = new Map(prev);
			const inner = new Map(next.get(fieldId));
			inner.set(uuid, value);
			next.set(fieldId, inner);
			return next;
		});
	}, []);
	const [advancedFilterOpen, setAdvancedFilterOpen] = React.useState(false);
	const [advancedFilterResult, setAdvancedFilterResult] = React.useState<FilterResult>(defaultFilters);
	// the filter a column header's icon opens directly
	const [selectedFilter, setSelectedFilter] = React.useState<FilterId | undefined>();
	const [hiddenColumns, rawSetHiddenColumns] = React.useState<string[] | undefined>(() => {
		try {
			const raw = localStorage.getItem('products_hidden_columns');
			return raw ? JSON.parse(raw) : undefined;
		} catch { return undefined; }
	});
	const setHiddenColumns = React.useCallback((value: string[]) => {
		const cleaned = value.filter(id => !PINNED_COLUMNS.includes(id));
		localStorage.setItem('products_hidden_columns', JSON.stringify(cleaned));
		rawSetHiddenColumns(cleaned);
	}, []);
	const [columnOrder, rawSetColumnOrder] = React.useState<string[]>(() => {
		try { return JSON.parse(localStorage.getItem('products_column_order') ?? '[]'); } catch { return []; }
	});
	// drag-resizable columns persisted like the promotions and companies tables
	const [tableResize, setTableResize] = React.useState<Record<string, number>>(() => {
		try { return JSON.parse(localStorage.getItem('table_products_resize') ?? '{}'); } catch { return {}; }
	});
	React.useEffect(() => {
		localStorage.setItem('table_products_resize', JSON.stringify(tableResize));
	}, [tableResize]);
	const setColumnOrder = React.useCallback((value: string[]) => {
		localStorage.setItem('products_column_order', JSON.stringify(value));
		rawSetColumnOrder(value);
	}, []);
	const [productFieldValues, setProductFieldValues] = React.useState<Map<number, Map<string, unknown>>>(new Map());
	const productFilters = React.useMemo(() => productFilterList(allFields, new Set(productFieldValues.keys())), [allFields, productFieldValues]);
	React.useEffect(() => {
		getProductFieldValues().then(rows => {
			const map = new Map<number, Map<string, unknown>>();
			rows.forEach(r => {
				if (!map.has(r.field_id)) map.set(r.field_id, new Map());
				map.get(r.field_id)?.set(r.product_uuid, r.value);
			});
			setProductFieldValues(map);
		}).catch(console.error);
	}, []);
	const advancedIds = React.useMemo((): Set<number> | undefined => {
		const tree = advancedFilterResult?.formatted;
		if (!tree || filterTreeLength(tree) === 0) return undefined;
		return new Set(allProducts.filter(p => matchProductTree(p, tree, productFieldValues, dimensionValues)).map(p => p.id));
	}, [advancedFilterResult, allProducts, productFieldValues, dimensionValues]);
	const history = useHistory();

	function checkUrlParams() {
		const productId = getUrlParameterByName('id');
		const mode = getUrlParameterByName('mode');
		if (productId) {
			switch (mode) {
				case 'detail':
					setModalState({ isOpen: true, fullOpenMode: false, data: { productId } });
					break;
				case 'default':
				default:
					setModalState({ isOpen: true, fullOpenMode: true, data: { productId } });
					break;
			}
		}
	}

	React.useEffect(() => {
		checkUrlParams();
		history.listen(checkUrlParams);
	}, [history]);


	// ----- views: filters + column order/visibility + sort, mirroring the
	// promotions listing
	const [views, setViews] = useRecoilState(AViews);
	const [isViewOpen, setIsViewOpen] = React.useState(false);
	const [editedView, setEditedView] = React.useState<DbView<ProductsView> | undefined>();
	const [viewDeletionState, setViewDeletionState] = React.useState<{ isOpen: boolean, data?: DbView<ProductsView> }>({ isOpen: false });
	const [selectedView, setSelectedView] = useFunctionState<DbView<ProductsView> | undefined>(views.products?.find(v => v.id.toString() === localStorage.getItem('products_view_id')),
		({ newValue }) => {
			if (newValue) {
				localStorage.setItem('products_view_id', newValue.id.toString());
			} else {
				localStorage.removeItem('products_view_id');
			}
			return newValue;
		}
	);
	// apply what a view's data allows and never let a malformed one (old
	// format, someone else's public view) break the click
	const applyView = React.useCallback((view: DbView<ProductsView> | undefined) => {
		let filters = defaultFilters;
		if (view?.data.filters) {
			try {
				filters = { ...view.data.filters, formatted: view.data.filters.values ? convertFilters(view.data.filters.values) : view.data.filters.formatted };
			} catch (e) {
				console.error('unreadable view filters', view.id, e);
				filters = view.data.filters;
			}
		}
		setAdvancedFilterResult(filters);
		if (view && Array.isArray(view.data.columns)) {
			setColumnOrder(view.data.columns.map(c => c.column));
			setHiddenColumns(view.data.columns.filter(c => c.hidden).map(c => c.column));
		}
		if (view?.data.sort?.id) setSort(view.data.sort);
	}, []);
	React.useEffect(() => {
		const view = views.products?.find(v => v.id.toString() === localStorage.getItem('products_view_id'));
		setSelectedView(view);
		if (view) applyView(view);
	}, [views]);
	// until the user configures columns, the secondary product columns and
	// every dimension column start hidden: the listing stays readable
	// whatever the number of fields
	const effectiveHiddenColumns = React.useMemo(
		() => hiddenColumns ?? [...DEFAULT_HIDDEN_COLUMNS, ...dimensionFields.map(f => `dim_${f.id}`)],
		[hiddenColumns, dimensionFields]);
	// values are fetched lazily: only the visible columns and the fields the
	// active filter tree references, merged into what previous fetches brought
	React.useEffect(() => {
		const needed = new Set(dimensionFields.filter(f => !effectiveHiddenColumns.includes(`dim_${f.id}`)).map(f => f.id));
		collectProductDimFieldIds(advancedFilterResult?.formatted).forEach(id => needed.add(id));
		const missing = [...needed].filter(id => !dimensionValues.has(id));
		if (missing.length === 0) return;
		getProductDimensionValues(missing)
			.then(fetched => setDimensionValues(prev => new Map([...prev, ...fetched])))
			.catch(console.error);
	}, [dimensionFields, effectiveHiddenColumns, advancedFilterResult, dimensionValues]);
	// the displayed column order: identity columns pinned first, actions last,
	// the saved order in between and new columns appended. Derived from the
	// static id list, never from the built columns: the organizer must exist
	// before them, since it renders inside the actions column's header
	const allColumnIds = React.useMemo(
		() => [...PINNED_START, 'reference', 'barcode', 'code_upc', 'brand', 'category', 'price', 'recommended_price', ...taxes.map(t => `tax_${t.id}`),
			'stock', 'outer', 'width', 'height', 'depth', 'status', 'created_at', 'updated_at', ...dimensionFields.map(f => `dim_${f.id}`), 'actions'],
		[dimensionFields, taxes]);
	const orderedColumnIds = React.useMemo(() => {
		const known = columnOrder.filter(id => allColumnIds.includes(id) && !PINNED_COLUMNS.includes(id));
		const rest = allColumnIds.filter(id => !PINNED_COLUMNS.includes(id) && !known.includes(id));
		return [...PINNED_START, ...known, ...rest, 'actions'];
	}, [allColumnIds, columnOrder]);
	const saveViewRef = React.useRef<{ columns: ViewCompanyColumns[], sort?: TableSortType }>({ columns: [] });
	React.useEffect(() => {
		saveViewRef.current = {
			columns: orderedColumnIds.map(id => ({ column: id, hidden: effectiveHiddenColumns.includes(id) })),
			sort,
		};
	}, [orderedColumnIds, effectiveHiddenColumns, sort]);
	const saveView = React.useCallback(async(v: ViewCreation) => {
		const data: NewView<'products'> = {
			data: {
				filters: advancedFilterResult ?? defaultFilters,
				name: v.name,
				type: 'products',
				...saveViewRef.current,
			},
			public: v.public
		};
		await (v.id ? putView(v.id, data) : postView(data));
		getViews().then(newViews => {
			setSelectedView(newViews.products.find(nv => v.id === nv.id || !views.products.find(pv => pv.id == nv.id)));
			setViews(newViews);
		});
	}, [views, advancedFilterResult]);
	const columnLabels = React.useMemo<Record<string, string>>(() => ({
		reference: translateToString('reference'),
		barcode: translateToString('EAN-JAN barcode'),
		code_upc: translateToString('UPC Barcode'),
		brand: translateToString('brand'),
		category: translateToString('category'),
		price: translateToString('price'),
		recommended_price: translateToString('recommended price'),
		stock: translateToString('stock'),
		outer: translateToString('outer'),
		width: `${translateToString('width')} (cm)`,
		height: `${translateToString('height')} (cm)`,
		depth: `${translateToString('depth')} (cm)`,
		status: translateToString('Avaibility'),
		created_at: translateToString('created_at'),
		updated_at: translateToString('updated_at'),
		...Object.fromEntries(taxes.map(t => [`tax_${t.id}`, t.name])),
		...Object.fromEntries(dimensionFields.map(f => [`dim_${f.id}`, f.name])),
	}), [dimensionFields, taxes]);
	const organizerColumns: OrganizerColumn[] = React.useMemo(() => orderedColumnIds
		.filter(id => !PINNED_COLUMNS.includes(id))
		.map((id): OrganizerColumn => ({
			label: columnLabels[id] ?? id,
			value: id,
			checked: !effectiveHiddenColumns.includes(id),
			sequence: orderedColumnIds.indexOf(id),
			categoryIndex: id.startsWith('dim_') ? 1 : 0,
		})), [orderedColumnIds, effectiveHiddenColumns, columnLabels]);

	const columns = React.useMemo(() => productcolumns(products, setProducts, () => setPagination({ ...pagination }), (product, fullOpenMode) => {
		history.push({
			search: `?id=${product.id}&mode=${fullOpenMode ? 'default' : 'detail'}`
		});
	}, { fields: dimensionFields, values: dimensionValues, uuidById: productUuidById, editable: exportCreatable, onSaved: onDimensionValueSaved }, { taxes, values: taxValues }, <ColumnOrganizerWrapper
		categories={['products.title', 'fields']}
		columns={organizerColumns}
		onSort={setColumnOrder}
		setHiddenColumns={setHiddenColumns}
	/>, openVision), [products, dimensionFields, dimensionValues, productUuidById, exportCreatable, onDimensionValueSaved, taxes, taxValues, organizerColumns, setColumnOrder, setHiddenColumns, openVision]);

	const openPopupImport = React.useCallback(() => importProducts(importCall => {
		if (!importCall) return;
		setLoadingState('loading');
		importCall().then(() => {
			// imported values invalidate the session caches wholesale
			clearProductDimensionValuesCache();
			setDimensionValues(new Map());
			setPagination(p => ({ ...p }));
			setLoadingState('loaded');
		}).catch(e => {
			console.error(e);
			setLoadingState('loaded');
			launchToastError(translateToString('import.products.failed'));
		});
	}, alert, allFields, () => undefined), [alert, allFields]);
	// the popup import replaces the classic import module once at least one
	// field is tagged Product; without such a field nothing changes
	const popupImport = dimensionFields.length > 0 && exportCreatable;

	React.useEffect(() => {
		const data: DropdownData[] = [];
		if (massDeletable) data.push({ label: <Translate id='Delete selection' />, value: 'delete' });
		if (exportCreatable) data.push({ label: <Translate id='export' />, value: 'export' });
		if (popupImport) data.push({ label: <Translate id='import.products.title' />, value: 'import_popup' });
		else if (isAllowedTo({ objectAction: 'CreateImport' })) data.push({ label: <Translate id='general.import' />, value: 'import' });

		props.setToolBarState({
			title: translate('general.products') as string,
			bottomLeftToolbarComponent: <>
				<ViewPicker
					type='products'
					noBorder
					selectedView={selectedView}
					views={views.products}
					onViewChange={view => {
						setSelectedView(view);
						applyView(view);
					}}
					onDelete={view => setViewDeletionState({ isOpen: true, data: view })}
					onCreate={() => { setSelectedView(undefined); setIsViewOpen(true); }}
					onEdit={() => setIsViewOpen(true)}
					setEditedView={setEditedView}
				/>
				<ToolbarFilterButton
					activeFilters={filterTreeLength(advancedFilterResult?.formatted)}
					onClick={() => {
						setSelectedFilter(undefined);
						setAdvancedFilterOpen(true);
					}}
					onDeleteFilter={() => setAdvancedFilterResult(defaultFilters)}
				/>
				{(!equal(advancedFilterResult?.formatted, selectedView?.data.filters?.formatted ?? defaultFilters.formatted)
					|| (selectedView?.data.columns
						? !equal(orderedColumnIds, selectedView.data.columns.map(c => c.column))
						|| !equal(effectiveHiddenColumns, selectedView.data.columns.filter(c => c.hidden).map(c => c.column))
						: hiddenColumns !== undefined || columnOrder.length > 0)
				) && <SidebarButton onClick={() => setIsViewOpen(true)}><Translate id='save_view' /></SidebarButton>}
				{loadingState == 'loading' && <LoaderContainer><PageLoader size={20} /></LoaderContainer>}
			</>,
			bottomRightToolbarComponent: <>
				{amISuperAdmin && <ToolbarToggle>
					<Switch value={visionMode} onChange={setVisionMode} />
					<Translate id='products.vision_mode' />
				</ToolbarToggle>}
				<ToolbarFilter
					category={AtomCategory.PRODUCTS}
					elements={[
						ToolbarElement.INPUT_SEARCH
					]}
				/>
				<Restricted to={{ objectAction: 'CreateCatalog' }}>
					<Add size='22px' onClick={() => setIsCreationOpen(true)} />
				</Restricted>
				<Restricted to={[
					{ objectAction: 'DeleteCatalog' },
					{ objectAction: 'CreateExport' },
				]}>
					<Dropdown dropdownStyle={{ optionLeft: '-115px', optionWidth: '160px' }} datalist={data} name={'options'}
						JSXButton={() => <ToolbarImageContainer marginLeft='10px' hasPointer><Ellipsis size={18} color={GreySidely3} strokeWidth={2.5} /></ToolbarImageContainer>}
						searchable={false}
						onChange={(value: DropdownData) => {
							switch (value.value) {
								case 'export':
									dispatch({ type: 'INSERT_MESSAGE', value: { message: { text: 'exports.ready_toast' }, state: 'loading' } });
									exportData(TypeFile.CSV, 'Products');
									break;
								case 'delete':
									if (products.filter(p => p.checked).length > 0) {
										// @ts-expect-error swal
										swal.fire({
											text: translate('Are you sure you want to delete selected data from the database?') as string,
											type: 'warning',
											confirmButtonColor: RedSidely
										})
											.then(result => {
												if (result.value) {
													updateProducts(products.filter(p => p.checked).map(p => ({ id: p.id, deleted: true })))
														.then(() => setPagination({ ...pagination }))
														.catch(console.log);
												}
											});
									} else {
										// @ts-expect-error swal
										swal.fire({
											title: 'Attention!',
											text: translate('No data selected !') as string,
											type: 'warning',
											showConfirmButton: false
										});
									}
									break;
								case 'import':
									history.push({ pathname: '/settings', hash: 'import' });
									break;
								case 'import_popup':
									openPopupImport();
									break;
								default:
									break;
							}
						}}
					/>
				</Restricted>
			</>
		});
	}, [products, loadingState, advancedFilterResult, views, selectedView, orderedColumnIds, hiddenColumns, effectiveHiddenColumns, columnOrder, organizerColumns, popupImport, openPopupImport, visionMode, amISuperAdmin]);

	React.useEffect(() => {
		setLoadingState('loading');
		fetchTaxValues();
		get_products({
			// With an advanced tree active, fetch one wide page and filter it
			// client-side (the tree semantics live in the products atom, like
			// the promotion picker); pagination is then applied locally.
			limit: advancedIds ? ADVANCED_FILTER_FETCH_LIMIT : pagination.step,
			offset: advancedIds ? 0 : pagination.offset,
			search: inputSearchText.length > 0 ? inputSearchText : undefined,
			sort
		})
			.then(res => {
				if (advancedIds) {
					const rows = res.data.products.filter(p => advancedIds.has(p.id));
					setMax(rows.length);
					setProducts(rows.slice(pagination.offset, pagination.offset + pagination.step));
				} else {
					setMax(res.data.count);
					setProducts(res.data.products);
				}
				setLoadingState('loaded');
			})
			.catch(e => {
				console.log(e);
				setLoadingState('error');
			});
	}, [pagination, inputSearchText, sort, advancedIds, fetchTaxValues]);

	// A filter change invalidates the current page offset.
	React.useEffect(() => {
		setPagination(p => ({ ...p, offset: 0 }));
	}, [advancedFilterResult]);


	const hasSearchOrFilters = inputSearchText.trim().length > 0 || filterTreeLength(advancedFilterResult?.formatted) > 0;

	return <Restricted to={{ objectAction: 'ReadCatalog' }} fallback={<Unhautorized />} >
		<Container>
			<FlexDiv flow='column' width='100%'>
				<Table height={`calc(100vh - ${TOOLBARS_HEIGHT + 50}px)`}
					columns={columns}
					data={products}
					onSort={sort => setSort(sort[0])}
					initialSortBy={sort}
					hiddenColumns={effectiveHiddenColumns}
					sortColumns={orderedColumnIds}
					EnableResize
					onResize={value => Object.keys(value).length > 0 && setTableResize(value)}
					resizeValue={tableResize}
					onClickFilter={id => {
						setSelectedFilter(id as FilterId);
						setAdvancedFilterOpen(true);
					}}
				/>
				{loadingState === 'loaded' && !products.length && <div style={{ position: 'absolute' }}>
					{hasSearchOrFilters
						? <NoData
							isNoResult
							message={translateToString('No results found')}
							messageButton={<Translate id='reset_filters' />}
							onClickButton={() => {
								setAdvancedFilterResult(defaultFilters);
								setInputSearchText('');
							}}
						/>
						: <NoData
							message={translateToString('no_products')}
							messageButton={<Translate id='general.import' />}
							messageButton2={<Translate id='export_model' />}
							onClickButton={() => popupImport ? openPopupImport() : history.push({ pathname: '/settings', hash: 'import' })}
							onClickButton2={() => {
								const a = document.createElement('a');
								a.href = getLanguageCode() === 'fr' ? ProductsTemplateFr : ProductsTemplateEn;
								a.download = 'products_template.csv';
								a.click();
							}}
						/>
					}
				</div>}
				<Pagination label={translate('general.products').toString()} amount={max} steps={[DEFAULT_LIMIT, 100, 200, 500]} onChange={(value) => setPagination({ ...pagination, ...value })} />
				<AdvancedFilters
					isOpen={advancedFilterOpen}
					setOpen={setAdvancedFilterOpen}
					filterList={[{ category: translateToString('general.products'), filters: productFilters }]}
					onChange={setAdvancedFilterResult}
					filterValues={advancedFilterResult?.values}
					selectedFilter={selectedFilter}
				/>
				<PopupView
					isOpen={isViewOpen}
					view={editedView ?? selectedView}
					onClickOut={() => { setIsViewOpen(false); setEditedView(undefined); }}
					onValidation={(v) => { saveView(v).then(() => setIsViewOpen(false)); setEditedView(undefined); }}
				/>
				<PopupDeletion
					records={1}
					translationKey='view'
					noVerification
					isOpen={viewDeletionState.isOpen}
					onClickOut={() => setViewDeletionState({ isOpen: false })}
					onValidation={() => {
						if (viewDeletionState.data) {
							deleteView(viewDeletionState.data.id).then(() => getViews().then(setViews));
						}
					}}
				/>
				<ProductPopup
					isOpen={modalState.isOpen}
					setIsOpen={isOpen => {
						if (!isOpen) { removeAllUrlParameters(); }
						setModalState({ ...modalState, isOpen });
					}}
					product={modalState.data?.product}
					productId={modalState.data?.productId}
					fullOpenMode={modalState.fullOpenMode}
					refresh={() => setPagination({ ...pagination })}
				/>
				<VisionPopup isOpen={visionProduct !== undefined} onClose={() => setVisionProduct(undefined)} product={visionProduct} />
				<Restricted to={{ objectAction: 'CreateCatalog' }}>
					<ProductCreation
						refresh={() => setPagination({ ...pagination })}
						isOpen={isCreationOpen}
						setIsOpen={(b: boolean) => setIsCreationOpen(b)}
					/>
				</Restricted>
			</FlexDiv>
		</Container>
	</Restricted>;
}
